/**
 * @file local-pickup-form-utils.ts
 *
 * Pure helpers for the local-pickup form: deriving the editable fields from a
 * pickup-location input, dirty comparison, and rebuilding a
 * {@link PickupLocationInput} from the fields (trimming text and preserving the seed's
 * `state`, which the form doesn't edit). Kept pure so the mapping is unit-testable
 * without rendering.
 */

import type { PickupLocationInput } from '../../api/shop-types';
import type { PickupFormFields } from './local-pickup-form-types';

/**
 * Derives the editable form fields from a pickup-location input.
 *
 * @param location - The source pickup-location input.
 *
 * @returns The initial {@link PickupFormFields}.
 */
export function derivePickupFields(location: PickupLocationInput): PickupFormFields {
  return {
    name: location.name,
    details: location.details,
    address1: location.address.address_1,
    city: location.address.city,
    postcode: location.address.postcode,
    country: location.address.country,
  };
}

/**
 * Shallow-compares two field sets for dirty tracking.
 *
 * @param a - One field set.
 * @param b - The other field set.
 *
 * @returns `true` when every field is strictly equal.
 */
export function shallowEqualPickup(a: PickupFormFields, b: PickupFormFields): boolean {
  const keys = Object.keys(a) as (keyof PickupFormFields)[];
  return keys.every((key) => a[key] === b[key]);
}

/**
 * Rebuilds a pickup-location input from the form fields, trimming text and preserving
 * the seed's `state` (which the form doesn't edit).
 *
 * @param fields - The current form fields.
 * @param base - The seed input (its `state` is carried over).
 *
 * @returns The {@link PickupLocationInput} to persist.
 */
export function buildPickupLocation(
  fields: PickupFormFields,
  base: PickupLocationInput,
): PickupLocationInput {
  return {
    name: fields.name.trim(),
    details: fields.details.trim(),
    address: {
      address_1: fields.address1.trim(),
      city: fields.city.trim(),
      state: base.address.state,
      postcode: fields.postcode.trim(),
      country: fields.country,
    },
  };
}
